import Vue from "vue";
import VueRouter, { RouterOptions } from "vue-router";
import posthog from 'posthog-js';
import store, { storeModules, EDIT_STORE_MODULES } from "../store";

import { Message } from 'element-ui';
import EtcRoutes from "./etc-routes";
import AppRoutes from './app-routes';
import EventBus from "../utils/event-bus";
import { EVENT_BUS, ROUTER } from "../common/constants";
import ModuleType from "../common/module-constant";
import i18n from "../i18n";

import KanbanWebsite from "../containers/kanban-website/KanbanWebsiteComponent.vue";
import EmailWebsite from "../containers/email-website/EmailWebsiteComponent.vue";
import Dashboard from "../containers/dashboard";
import AssistantArtifact from "../containers/assistant-artifact/AssistantArtifactComponent.vue";

Vue.use(VueRouter);

// Define kanban routes
const kanbanWebsiteRoute = 
    {
        path: "/kanban-website",
        name: ROUTER.KANBAN,
        component: KanbanWebsite,
        meta: {
            title_key: "KANBAN_BETA",
            icon_name: "el-icon-s-grid",
            standalone: true,
            moduleType: ModuleType.Kanban
        },
        props: {},
        beforeEnter: (to, from, next) => {
            const isKanbanFlagEnabled = posthog.isFeatureEnabled('kanban-board')
            if (isKanbanFlagEnabled) {
              next()
            } else {
              next(false)
            }
        }
    }

const emailWebsiteRoute = 
    {
        path: "/emails-website",
        name: ROUTER.EMAIL_WEBSITE,
        component: EmailWebsite,
        meta: {
            keepAlive: true,
            title_key: "EMAILS_BETA",
            icon_name: "el-icon-s-promotion",
            standalone: true,
            moduleType: ModuleType.EmailWebsite
        },
        props: {},
        beforeEnter: (to, from, next) => {
            const isEmailFlagEnabled = posthog.isFeatureEnabled('automation-editor')
            if (isEmailFlagEnabled) {
              next()
            } else {
              next(false)
            }
        }
    }

const dashboardRoute =
    {
        path: "/dashboard",
        name: ROUTER.DASHBOARD,
        component: Dashboard,
        meta: {
            title_key: "DASHBOARD",
            icon_name: "ico_dashboard",
            standalone: true,
            moduleType: ModuleType.Dashboard
        },
        props: {},
        beforeEnter: (to, from, next) => {
            const isDashboardFlagEnabled = posthog.isFeatureEnabled('dashboard-visible-flag')
            if (isDashboardFlagEnabled) {
              next()
            } else {
              next(false)
            }
        }
    }

const assistantArtifactRoute = {
    path: "/assistant-artifact/:id",
    name: ROUTER.ASSISTANT_ARTIFACT,
    component: AssistantArtifact,
    meta: {
        title_key: "ASSISTANT_ARTIFACT",
        standalone: true,
        moduleType: ModuleType.AssistantArtifact,
    },
    props: {},
    beforeEnter: (to, from, next) => {
        const isAssistantFlagEnabled = posthog.isFeatureEnabled('ai-assistant')
        if (isAssistantFlagEnabled) {
          next()
        } else {
          next(false)
        }
    }
};

// Build base routes (without kanban initially)
let routes = [
    ...AppRoutes,
    ...EtcRoutes,
];

let config: RouterOptions = {
    routes: routes,
    linkActiveClass: "active"
};

const historyModeEl = document.querySelector("meta[name='History-Mode']");

if (historyModeEl || location.port == '82') {
    config.mode = "history";
    config.base = "/";
}

const router: VueRouter = new VueRouter(config);
const commonModule = `${storeModules.Common}`;

// Flag to track if kanban routes have been added
let kanbanRoutesAdded = false;
let emailWebsiteRoutesAdded = false;
let dashboardRoutesAdded = false;
let assistantArtifactRoutesAdded = false;

// Function to add kanban routes dynamically
function addKanbanRoutes() {
    if (!kanbanRoutesAdded && posthog.isFeatureEnabled('kanban-board')) {
        console.log('Adding kanban routes dynamically');
        router.addRoute(kanbanWebsiteRoute as any);
        kanbanRoutesAdded = true;
        return true;
    }
    return false;
}

function addDashboardRoutes() {
    if (!dashboardRoutesAdded && posthog.isFeatureEnabled('dashboard-visible-flag')) {
        console.log('Adding dashboard routes dynamically');
        router.addRoute(dashboardRoute as any);
        dashboardRoutesAdded = true;
        return true;
    }
    return false;
}

function addEmailWebsiteRoutes() {
    if (!emailWebsiteRoutesAdded && posthog.isFeatureEnabled('automation-editor')) {
        console.log('Adding email website routes dynamically');
        router.addRoute(emailWebsiteRoute as any);
        emailWebsiteRoutesAdded = true;
        return true;
    }
    return false;
}

export function addAssistantArtifactRoutes() {
    if (!assistantArtifactRoutesAdded && posthog.isFeatureEnabled('ai-assistant')) {
        console.log('Adding assistant artifact routes dynamically');
        router.addRoute(assistantArtifactRoute as any);
        assistantArtifactRoutesAdded = true;
        return true;
    }
    return false;
}

// Check for PostHog readiness and add routes
function waitForPostHogAndAddRoutes() {
    // Try immediately first — call each independently to avoid && short-circuit
    const allAdded =
        [addKanbanRoutes(), addEmailWebsiteRoutes(), addDashboardRoutes(), addAssistantArtifactRoutes()]
            .every(Boolean);
    if (allAdded) {
        return;
    }

    // If PostHog has onFeatureFlags method, use it
    if (typeof posthog.onFeatureFlags === 'function') {
        posthog.onFeatureFlags(() => {
            addKanbanRoutes();
            addEmailWebsiteRoutes();
            addDashboardRoutes();
            addAssistantArtifactRoutes();
        });
    } else {
        // Fallback: poll for feature flag availability
        let retryCount = 0;
        const maxRetries = 10;
        const retryInterval = 1000;

        const checkInterval = setInterval(() => {
            retryCount++;
            console.log(`Checking PostHog readiness - Attempt ${retryCount}`);

            const allDone =
                [addKanbanRoutes(), addEmailWebsiteRoutes(), addDashboardRoutes(), addAssistantArtifactRoutes()]
                    .every(Boolean);
            if (allDone) {
                clearInterval(checkInterval);
            } else if (retryCount >= maxRetries) {
                console.log('PostHog feature flags not available after maximum retries');
                clearInterval(checkInterval);
            }
        }, retryInterval);
    }
}

// Start checking for PostHog readiness
waitForPostHogAndAddRoutes();

// Global error handling for NavigationDuplicated
const originalPush = router.push;
router.push = function push(location) {
    return originalPush.call(this, location)?.catch(err => {
        if (err.name !== 'NavigationDuplicated' && !err.message.includes('Navigation cancelled')) {
            throw err;
        }
    });
};

const originalReplace = router.replace;
router.replace = function replace(location) {
    return originalReplace.call(this, location)?.catch(err => {
        if (err.name !== 'NavigationDuplicated') {
            throw err;
        }
    });
};

// BeforeEach guard to check for open drawers and saving state
router.beforeEach((to, from, next) => {
    // Prevent navigation while a save is in progress
    const isSaving = EDIT_STORE_MODULES.some(m => store.state[m]?.isSaving);
    if (isSaving) {
        Message.warning(i18n.t('SAVING_IN_PROGRESS').toString());
        next(false);
        return;
    }

    const drawers = store.state[commonModule].drawerModels;
    if (drawers.length > 0) {
        EventBus.$emit(EVENT_BUS.COMMON.WARNING_CLOSE_MODAL, to);
        return;
    }
    next();
});

export default router;